/**
 * <StageProgramPage> — le programme d'un stage acheté, que la famille adapte en une phrase (page).
 *
 * Le haut dit qui, quand, avec qui et de quoi le programme est fait (`<StageProgramHeader>`) ; les
 * séances suivent par jour (`<RevisionProgramList>`). « Recomposer » estompe le programme et pose
 * par-dessus trois étapes qui se cochent (`<RecomposeSteps>`) ; à la dernière, la page montre le
 * programme recomposé, dont les chapitres nouveaux portent « selon votre demande ».
 *
 * Pure composition : les données arrivent en props, les gestes repartent en callbacks.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Banner } from "../../atoms/Banner"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Stack } from "../../atoms/containers/Stack"
import { Header } from "../../molecules/Header"
import { RecomposeSteps } from "../../molecules/RecomposeSteps"
import { Card } from "../../molecules/Card"
import { EmptyState } from "../../molecules/EmptyState"
import { AdaptProgramSheet, type AdaptProgramSheetProps } from "../../organisms/AdaptProgramSheet"
import { StageProgramHeader } from "../../organisms/StageProgramHeader"
import { PageTemplate } from "../../templates/PageTemplate"
import { RevisionProgramList } from "../../templates/RevisionProgramList"
import { StageProgramSkeleton } from "./StageProgramSkeleton"
import { type UIStageProgram } from "../../../ports/primary/ui/types"

/** What the recomposition does, in three steps — the last ticks when the answer is there. */
const RECOMPOSE_STEPS = ["On lit votre demande", "On choisit dans le programme officiel", "On répartit sur la semaine"]

export type StageProgramPageProps = {
    program: UIStageProgram | null
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** Why the last gesture did not go through, when no sheet is open to say it. */
    notice?: string | null
    onClose?: () => void
    onAdapt: () => void
    onReset: () => void
    /** A recomposition is under way: the programme fades under the steps. */
    recomposing: boolean
    /** Its answer is there: the last step may tick. */
    recomposeDone: boolean
    onRecomposeFinished: () => void
    sheet: Omit<AdaptProgramSheetProps, "childFirstname">
}

export function StageProgramPage(props: StageProgramPageProps) {
    const {
        program,
        loading,
        error,
        onRetry,
        notice,
        onClose,
        onAdapt,
        onReset,
        recomposing,
        recomposeDone,
        onRecomposeFinished,
        sheet,
    } = props

    const header = (
        <Header
            title="Programme de révision"
            left={<IconButton iconName="chevron-left" appearance="transparent" iconColor="primary" onPress={onClose} />}
        />
    )

    return (
        <PageTemplate
            header={header}
            loading={loading}
            skeleton={<StageProgramSkeleton />}
            error={error}
            onRetry={onRetry}
        >
            {program == null && (
                <EmptyState
                    iconName="calendar"
                    title="Pas de programme de stage"
                    message="Il s'affiche ici dès que le stage est réservé."
                />
            )}
            {program != null && (
                <Stack gap="6">
                    {notice != null && <Banner message={notice} icon="exclamation-circle" color="warning" tinted />}
                    <StageProgramHeader program={program} onAdapt={onAdapt} onReset={onReset} busy={recomposing} />
                    <View>
                        <View style={styles.list(recomposing)}>
                            <RevisionProgramList groups={program.groups} />
                        </View>
                        {recomposing && (
                            <View style={styles.overlay}>
                                <Card elevation="floating">
                                    <RecomposeSteps
                                        steps={RECOMPOSE_STEPS}
                                        done={recomposeDone}
                                        onFinished={onRecomposeFinished}
                                    />
                                </Card>
                            </View>
                        )}
                    </View>
                </Stack>
            )}
            <AdaptProgramSheet {...sheet} childFirstname={program?.childFirstname ?? null} />
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    list: (dimmed: boolean) => ({
        opacity: dimmed ? 0.35 : 1,
    }),
    overlay: {
        position: "absolute",
        top: theme.spacing["6"],
        left: theme.spacing["4"],
        right: theme.spacing["4"],
    },
}))
